import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { projectServices, withdrawalServices } from '../../../services'
import {
  DashboardHoldings,
  OpportunityDetails,
  WithdrawalApprovalDetails,
} from '../../../lib/types/propsType_v1'
import { displayDollar } from '../../../lib/utils/helpers'
import { withUpperScaleSuffix } from '../dashboard-v2.utils'
import { showBanner } from '../../../global-state/banner-slice'
import { PLATFORM_WITHDRAWAL_FEE } from '../../withdraw/constants'
import { PendingIcon } from '../../../lib/components'
import { WithdrawalNoticeModal } from '../../withdraw/WithdrawalNoticeModal'

type RedeemMode = 'amount' | 'units'

type WithdrawalRequestFormModalProps = {
  isOpen: boolean
  holding: DashboardHoldings | null
  investorDisplayName: string
  clientId?: string
  userId: number
  authToken: string
  onClose: () => void
  onPlacedSuccess?: () => void | Promise<void>
}

/** displayDollar treats the numeric string as cents */
const formatUsdFromCents = (cents: number) =>
  withUpperScaleSuffix(displayDollar(String(Math.round(cents)), 'USD', 2, false))

const addCalendarDays = (days: number) => {
  const d = new Date()
  d.setDate(d.getDate() + days)
  return d
}

const formatUnits = (units: number) =>
  units.toLocaleString(undefined, { maximumFractionDigits: 4, minimumFractionDigits: 0 })

const getNavPerUnitCents = (holding: DashboardHoldings) => {
  if (holding.current_nav > 0) return holding.current_nav
  return 0
}

const roundDownUnits = (units: number) => Math.floor(units * 10000) / 10000

const formatMaxDollarsForInput = (cents: number) => {
  const dollars = Math.floor((cents / 100) * 10) / 10
  return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(1)
}

const formatMaxUnitsForInput = (units: number) => {
  const rounded = roundDownUnits(units)
  return Number.isInteger(rounded) ? String(rounded) : String(rounded)
}

const toFiniteCents = (value: number | null | undefined): number | null =>
  value !== null && value !== undefined && Number.isFinite(value) ? value : null

export const WithdrawalRequestFormModal: React.FC<WithdrawalRequestFormModalProps> = ({
  isOpen,
  holding,
  investorDisplayName,
  clientId,
  userId,
  authToken,
  onClose,
  onPlacedSuccess,
}) => {
  const dispatch = useDispatch()
  const [opportunity, setOpportunity] = useState<OpportunityDetails | null>(null)
  const [maxWithdrawalCents, setMaxWithdrawalCents] = useState(0)
  const [maxWithdrawalUnits, setMaxWithdrawalUnits] = useState(0)
  const [hasPendingRedemption, setHasPendingRedemption] = useState(false)
  const [unitPriceCents, setUnitPriceCents] = useState(0)
  const [predictDetails, setPredictDetails] = useState<WithdrawalApprovalDetails | null>(null)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showSuccessNotice, setShowSuccessNotice] = useState(false)
  const [redeemMode, setRedeemMode] = useState<RedeemMode>('amount')
  const [withdrawalAmountInput, setWithdrawalAmountInput] = useState('')
  const [withdrawalUnitsInput, setWithdrawalUnitsInput] = useState('')

  const resetAndClose = useCallback(() => {
    setOpportunity(null)
    setMaxWithdrawalCents(0)
    setMaxWithdrawalUnits(0)
    setUnitPriceCents(0)
    setPredictDetails(null)
    setRedeemMode('amount')
    setWithdrawalAmountInput('')
    setWithdrawalUnitsInput('')
    setLoading(false)
    setSubmitting(false)
    setShowSuccessNotice(false)
    onClose()
  }, [onClose])

  useEffect(() => {
    if (isOpen && holding) {
      setRedeemMode('amount')
      setWithdrawalAmountInput('')
      setWithdrawalUnitsInput('')
    }
  }, [isOpen, holding?.opportunity_id])

  useEffect(() => {
    if (!isOpen || !holding) return

    let cancelled = false
    const load = async () => {
      setLoading(true)
      const headers = { Authorization: authToken }
      const idStr = String(holding.opportunity_id)

      try {
        const canLoadUnits = (holding.total_units ?? 0) > 0
        const [oppRes, maxRes, maxUnitsRes] = await Promise.all([
          projectServices.getOpportunityById(idStr, headers),
          withdrawalServices.getMaxWithdrawalAmountByOpportunity(idStr, headers),
          canLoadUnits
            ? withdrawalServices.getMaxWithdrawalUnitsByOpportunity(idStr, headers)
            : Promise.resolve(null),
        ])

        if (cancelled) return

        if (oppRes?.status === 200 && oppRes?.data) {
          setOpportunity(oppRes.data)
        } else {
          setOpportunity(null)
          dispatch(
            showBanner({
              text: 'Could not load opportunity details for withdrawal.',
              variant: 'error-banner',
            }),
          )
        }

        setHasPendingRedemption(Boolean(maxRes?.data?.hasPendingRedemption))

        if (maxRes?.status === 200 && maxRes?.data?.maxAmount !== null) {
          const raw = Number(maxRes.data.maxAmount)
          setMaxWithdrawalCents(Number.isFinite(raw) ? Math.max(0, Math.floor(raw)) : 0)
        } else {
          setMaxWithdrawalCents(0)
        }

        let resolvedUnitPrice = 0
        if (maxUnitsRes?.status === 200 && maxUnitsRes?.data) {
          const rawUnits = Number(maxUnitsRes.data.maxUnits)
          setMaxWithdrawalUnits(
            Number.isFinite(rawUnits) ? Math.max(0, roundDownUnits(rawUnits)) : 0,
          )
          const rawPrice = Number(maxUnitsRes.data.unitPrice)
          if (Number.isFinite(rawPrice) && rawPrice > 0) {
            resolvedUnitPrice = Math.floor(rawPrice)
          }
        } else {
          setMaxWithdrawalUnits(0)
        }

        if (resolvedUnitPrice <= 0 && maxRes?.status === 200 && maxRes?.data?.unitPrice) {
          const rawPrice = Number(maxRes.data.unitPrice)
          if (Number.isFinite(rawPrice) && rawPrice > 0) {
            resolvedUnitPrice = Math.floor(rawPrice)
          }
        }

        if (resolvedUnitPrice <= 0) {
          resolvedUnitPrice = getNavPerUnitCents(holding)
        }
        setUnitPriceCents(resolvedUnitPrice)
      } catch {
        if (!cancelled) {
          dispatch(
            showBanner({ text: 'Failed to load withdrawal information.', variant: 'error-banner' }),
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [isOpen, holding, authToken, dispatch])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') resetAndClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, resetAndClose])

  const totalInvestedCents = holding?.invested_amount ?? 0
  const totalUnits = holding?.total_units ?? 0
  const holdingNavPerUnitCents = holding ? getNavPerUnitCents(holding) : 0
  const navPerUnitCents = unitPriceCents > 0 ? unitPriceCents : holdingNavPerUnitCents
  const canRedeemByUnits = totalUnits > 0 && navPerUnitCents > 0
  const noticeDays = opportunity?.assetClassData?.days_from_withdraw_request_to_payment ?? null

  const withdrawalNum = useMemo(() => {
    const n = parseFloat(withdrawalAmountInput.replace(/,/g, ''))
    return Number.isFinite(n) ? n : NaN
  }, [withdrawalAmountInput])

  const withdrawalUnitsNum = useMemo(() => {
    const n = parseFloat(withdrawalUnitsInput.replace(/,/g, ''))
    return Number.isFinite(n) ? n : NaN
  }, [withdrawalUnitsInput])

  const roundedWithdrawalDollars = useMemo(() => {
    if (redeemMode === 'units') {
      if (Number.isNaN(withdrawalUnitsNum) || withdrawalUnitsNum < 0 || navPerUnitCents <= 0) {
        return NaN
      }
      const rawDollars = (withdrawalUnitsNum * navPerUnitCents) / 100
      return Math.floor(rawDollars * 10) / 10
    }
    if (Number.isNaN(withdrawalNum) || withdrawalNum < 0) return NaN
    return Math.floor(withdrawalNum * 10) / 10
  }, [redeemMode, withdrawalNum, withdrawalUnitsNum, navPerUnitCents])

  const submitWithdrawalCents = useMemo(() => {
    if (Number.isNaN(roundedWithdrawalDollars)) return NaN
    return Math.round(roundedWithdrawalDollars * 100)
  }, [roundedWithdrawalDollars])

  const roundedWithdrawalUnits = useMemo(() => {
    if (redeemMode !== 'units' || Number.isNaN(withdrawalUnitsNum) || withdrawalUnitsNum < 0) {
      return NaN
    }
    return roundDownUnits(withdrawalUnitsNum)
  }, [redeemMode, withdrawalUnitsNum])

  const effectiveMaxWithdrawalUnits = useMemo(() => {
    if (maxWithdrawalUnits > 0) return maxWithdrawalUnits
    if (!canRedeemByUnits) return 0
    const fromAmount = roundDownUnits(maxWithdrawalCents / navPerUnitCents)
    return Math.min(totalUnits, fromAmount)
  }, [maxWithdrawalUnits, canRedeemByUnits, maxWithdrawalCents, navPerUnitCents, totalUnits])

  const maxWithdrawalDollarsForInput = useMemo(() => {
    if (maxWithdrawalCents <= 0) return NaN
    return Math.floor((maxWithdrawalCents / 100) * 10) / 10
  }, [maxWithdrawalCents])

  const expectedPayoutDate = useMemo(() => {
    if (predictDetails?.amountReceivableDate) {
      return new Date(predictDetails.amountReceivableDate)
    }
    if (noticeDays === null || noticeDays < 0) return null
    return addCalendarDays(noticeDays)
  }, [predictDetails?.amountReceivableDate, noticeDays])

  const canFetchPredictDetails = useMemo(() => {
    if (redeemMode === 'amount') {
      return (
        Number.isFinite(submitWithdrawalCents) &&
        submitWithdrawalCents > 0 &&
        submitWithdrawalCents <= maxWithdrawalCents &&
        maxWithdrawalCents > 0
      )
    }
    return (
      Number.isFinite(roundedWithdrawalUnits) &&
      roundedWithdrawalUnits > 0 &&
      roundedWithdrawalUnits <= effectiveMaxWithdrawalUnits &&
      effectiveMaxWithdrawalUnits > 0
    )
  }, [
    redeemMode,
    submitWithdrawalCents,
    maxWithdrawalCents,
    roundedWithdrawalUnits,
    effectiveMaxWithdrawalUnits,
  ])

  useEffect(() => {
    if (!isOpen || !holding || loading || !canFetchPredictDetails) {
      setPredictDetails(null)
      return
    }

    let cancelled = false
    const idStr = String(holding.opportunity_id)
    const headers = { Authorization: authToken }

    const timeoutId = setTimeout(async () => {
      try {
        const response =
          redeemMode === 'units'
            ? await withdrawalServices.getWithdrawalPredictApprovalDetailsByUnits(
                idStr,
                String(roundedWithdrawalUnits),
                headers,
              )
            : await withdrawalServices.getWithdrawalPredictApprovalDetails(
                idStr,
                String(submitWithdrawalCents),
                headers,
              )

        if (cancelled) return

        if (response?.status === 200 && response?.data) {
          setPredictDetails(response.data)
        } else {
          setPredictDetails(null)
        }
      } catch {
        if (!cancelled) setPredictDetails(null)
      }
    }, 500)

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
    }
  }, [
    isOpen,
    holding,
    loading,
    authToken,
    redeemMode,
    canFetchPredictDetails,
    submitWithdrawalCents,
    roundedWithdrawalUnits,
  ])

  const balanceAfterWithdrawalCents = useMemo(() => {
    if (Number.isNaN(submitWithdrawalCents) || submitWithdrawalCents < 0) return null
    return Math.max(0, totalInvestedCents - submitWithdrawalCents)
  }, [totalInvestedCents, submitWithdrawalCents])

  const effectiveWithdrawalUnits = useMemo(() => {
    if (!canRedeemByUnits) return NaN
    if (redeemMode === 'units') {
      if (Number.isNaN(roundedWithdrawalUnits) || roundedWithdrawalUnits < 0) return NaN
      return roundedWithdrawalUnits
    }
    if (Number.isNaN(submitWithdrawalCents) || submitWithdrawalCents < 0 || navPerUnitCents <= 0) {
      return NaN
    }
    return roundDownUnits(submitWithdrawalCents / navPerUnitCents)
  }, [canRedeemByUnits, redeemMode, roundedWithdrawalUnits, submitWithdrawalCents, navPerUnitCents])

  const balanceAfterWithdrawalUnits = useMemo(() => {
    if (
      !canRedeemByUnits ||
      Number.isNaN(effectiveWithdrawalUnits) ||
      effectiveWithdrawalUnits < 0
    ) {
      return null
    }
    return Math.max(0, roundDownUnits(totalUnits - effectiveWithdrawalUnits))
  }, [canRedeemByUnits, totalUnits, effectiveWithdrawalUnits])

  const amountValid =
    redeemMode === 'amount'
      ? Number.isFinite(submitWithdrawalCents) &&
        submitWithdrawalCents > 0 &&
        submitWithdrawalCents <= maxWithdrawalCents &&
        maxWithdrawalCents > 0
      : Number.isFinite(roundedWithdrawalUnits) &&
        roundedWithdrawalUnits > 0 &&
        roundedWithdrawalUnits <= effectiveMaxWithdrawalUnits &&
        effectiveMaxWithdrawalUnits > 0

  const exceedsMaxWithdrawal =
    redeemMode === 'amount'
      ? maxWithdrawalCents > 0 &&
        Number.isFinite(submitWithdrawalCents) &&
        submitWithdrawalCents > maxWithdrawalCents
      : effectiveMaxWithdrawalUnits > 0 &&
        Number.isFinite(roundedWithdrawalUnits) &&
        roundedWithdrawalUnits > effectiveMaxWithdrawalUnits

  const canRedeemAll =
    redeemMode === 'amount' ? maxWithdrawalCents > 0 : effectiveMaxWithdrawalUnits > 0

  const isRedeemingAll =
    redeemMode === 'amount'
      ? Number.isFinite(roundedWithdrawalDollars) &&
        roundedWithdrawalDollars === maxWithdrawalDollarsForInput
      : Number.isFinite(roundedWithdrawalUnits) &&
        roundedWithdrawalUnits === effectiveMaxWithdrawalUnits

  const handleRedeemAll = useCallback(() => {
    if (redeemMode === 'amount') {
      if (!Number.isFinite(maxWithdrawalDollarsForInput)) return
      setWithdrawalAmountInput(formatMaxDollarsForInput(maxWithdrawalCents))
      return
    }
    if (effectiveMaxWithdrawalUnits <= 0) return
    setWithdrawalUnitsInput(formatMaxUnitsForInput(effectiveMaxWithdrawalUnits))
  }, [redeemMode, maxWithdrawalCents, maxWithdrawalDollarsForInput, effectiveMaxWithdrawalUnits])

  const predictedWithdrawalCents = toFiniteCents(predictDetails?.withdrawalAmount)

  const handleSubmit = async () => {
    if (!holding || !opportunity || !amountValid || submitting) return

    setSubmitting(true)
    const roundedAmount =
      predictedWithdrawalCents !== null
        ? Math.floor((predictedWithdrawalCents / 100) * 10) / 10
        : roundedWithdrawalDollars
    const amountCents =
      predictedWithdrawalCents !== null
        ? Math.round(predictedWithdrawalCents)
        : submitWithdrawalCents

    const metadata = {
      completedSteps: 1,
      clientName: investorDisplayName,
      clientId: clientId ?? String(userId),
      totalInvestedAmount: totalInvestedCents / 100,
      withdrawalAmount: roundedAmount,
      redeemMode,
      ...(redeemMode === 'units' && Number.isFinite(roundedWithdrawalUnits)
        ? { withdrawalUnits: roundedWithdrawalUnits }
        : {}),
      underlyingAsset: holding.opportunity_title,
      withdrawalNoticePeriodDays: noticeDays,
      expectedDateOfFundsPayout: expectedPayoutDate?.toISOString().slice(0, 10) ?? null,
      exitDealingChargesUsd: PLATFORM_WITHDRAWAL_FEE,
      platformWithdrawalFeeUsd: PLATFORM_WITHDRAWAL_FEE,
    }

    try {
      const response = await withdrawalServices.postOpportunityWithdrawal(
        {
          opportunityId: String(holding.opportunity_id),
          investorId: String(userId),
          ...(redeemMode === 'units'
            ? { withdrawalQuantity: roundedWithdrawalUnits }
            : { withdrawalAmount: amountCents }),
          metadata,
        },
        { Authorization: authToken },
      )

      if (response?.status !== 200 || !response?.data?.id) {
        const message = response?.data?.message || 'Could not submit redemption request.'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
        return
      }

      const placeResponse = await withdrawalServices.patchOpportunityWithdrawal(
        Number(response.data.id),
        {
          metadata: {
            completedSteps: 2,
            receivedAmount: Math.round((roundedAmount - PLATFORM_WITHDRAWAL_FEE) * 100),
          },
        },
        { Authorization: authToken },
      )

      if (placeResponse?.status === 200) {
        setShowSuccessNotice(true)
      } else {
        const message =
          placeResponse?.data?.message || 'Could not place redemption request. Please try again.'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
      }
    } catch {
      dispatch(
        showBanner({ text: 'Could not submit redemption request.', variant: 'error-banner' }),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleDismissSuccessNotice = async () => {
    setShowSuccessNotice(false)
    resetAndClose()
    await onPlacedSuccess?.()
  }

  const clientIdDisplay = clientId || `User ID ${userId}`
  const remainingMoneyDisplay =
    balanceAfterWithdrawalCents === null ? '—' : formatUsdFromCents(balanceAfterWithdrawalCents)
  const remainingUnitsDisplay =
    balanceAfterWithdrawalUnits === null ? '—' : formatUnits(balanceAfterWithdrawalUnits)
  const withdrawalPreviewDisplay =
    predictedWithdrawalCents !== null
      ? formatUsdFromCents(Math.round(predictedWithdrawalCents))
      : Number.isFinite(roundedWithdrawalDollars) && roundedWithdrawalDollars > 0
      ? formatUsdFromCents(Math.round(roundedWithdrawalDollars * 100))
      : '—'

  if (!isOpen || !holding) return null

  return (
    <div
      className='dashboard-v2__withdrawal-modal-backdrop'
      role='presentation'
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting && !showSuccessNotice) resetAndClose()
      }}
    >
      {!showSuccessNotice ? (
        <div
          className='dashboard-v2__withdrawal-form'
          role='dialog'
          aria-modal='true'
          aria-labelledby='withdrawal-form-title'
          onClick={(e) => e.stopPropagation()}
        >
          <header className='dashboard-v2__withdrawal-form__header'>
            <div className='dashboard-v2__withdrawal-form__header-text'>
              <p className='dashboard-v2__withdrawal-form__eyebrow'>Redeem holding</p>
              <h2 id='withdrawal-form-title'>Redemption Request</h2>
              <div className='dashboard-v2__withdrawal-form__asset-chip'>
                <span className='dashboard-v2__withdrawal-form__asset-chip-title'>
                  {holding.opportunity_title}
                </span>
                {holding.asset_class ? (
                  <span className='dashboard-v2__withdrawal-form__asset-chip-badge'>
                    {holding.asset_class}
                  </span>
                ) : null}
              </div>
              <p className='dashboard-v2__withdrawal-form__subtitle'>
                Please confirm your details. You may redeem on an amount basis or on an unit basis,
                and receive the equivalent value.
              </p>
            </div>
            <div
              role='button'
              tabIndex={0}
              aria-label='Close dialog'
              className='dashboard-v2__holdings-actions-panel__close'
              onClick={resetAndClose}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  resetAndClose()
                }
              }}
            >
              ×
            </div>
          </header>

          {loading ? (
            <div className='dashboard-v2__withdrawal-form__loading' aria-busy='true'>
              <span className='dashboard-v2__withdrawal-form__loading-spinner' aria-hidden />
              <p>Loading redemption details…</p>
            </div>
          ) : (
            <div className='dashboard-v2__withdrawal-form__form'>
              <section className='dashboard-v2__withdrawal-form__section'>
                <h3 className='dashboard-v2__withdrawal-form__section-title'>Account</h3>
                <div className='dashboard-v2__withdrawal-form__account-grid'>
                  <div className='dashboard-v2__withdrawal-form__stat'>
                    <span className='dashboard-v2__withdrawal-form__stat-label'>Client</span>
                    <span className='dashboard-v2__withdrawal-form__stat-value'>
                      {investorDisplayName}
                    </span>
                  </div>
                  <div className='dashboard-v2__withdrawal-form__stat'>
                    <span className='dashboard-v2__withdrawal-form__stat-label'>Client ID</span>
                    <span className='dashboard-v2__withdrawal-form__stat-value'>
                      {clientIdDisplay}
                    </span>
                  </div>
                  <div className='dashboard-v2__withdrawal-form__stat dashboard-v2__withdrawal-form__stat--highlight'>
                    <span className='dashboard-v2__withdrawal-form__stat-label'>
                      Total invested
                    </span>
                    <span className='dashboard-v2__withdrawal-form__stat-value'>
                      {formatUsdFromCents(totalInvestedCents)}
                    </span>
                  </div>
                  {canRedeemByUnits ? (
                    <div className='dashboard-v2__withdrawal-form__stat'>
                      <span className='dashboard-v2__withdrawal-form__stat-label'>
                        Total units held
                      </span>
                      <span
                        className='dashboard-v2__withdrawal-form__stat-value'
                        data-testid='withdrawal-total-units-held'
                      >
                        {formatUnits(totalUnits)}
                      </span>
                    </div>
                  ) : null}
                </div>
              </section>

              <section
                className={`dashboard-v2__withdrawal-form__section dashboard-v2__withdrawal-form__section--primary${
                  redeemMode === 'units' ? ' dashboard-v2__withdrawal-form__section--units' : ''
                }`}
              >
                <h3 className='dashboard-v2__withdrawal-form__section-title'>Redemption</h3>
                {hasPendingRedemption ? (
                  <p
                    className='dashboard-v2__withdrawal-form__footer-note'
                    role='alert'
                    data-testid='withdrawal-pending-notice'
                  >
                    You already have a redemption in progress for this opportunity. You can request
                    another once it is executed or cancelled.
                  </p>
                ) : null}
                <div
                  className={`dashboard-v2__withdrawal-form__redeem-panel${
                    redeemMode === 'units'
                      ? ' dashboard-v2__withdrawal-form__redeem-panel--units'
                      : ''
                  }`}
                >
                  <div className='dashboard-v2__withdrawal-form__label-row'>
                    <label
                      htmlFor={
                        redeemMode === 'amount' ? 'wr-withdrawal-amt' : 'wr-withdrawal-units'
                      }
                    >
                      {redeemMode === 'amount' ? 'Redemption Amount' : 'Redemption Units'}
                    </label>
                    {canRedeemAll && (
                      <button
                        type='button'
                        className={
                          isRedeemingAll
                            ? 'dashboard-v2__withdrawal-form__redeem-all dashboard-v2__withdrawal-form__redeem-all--active'
                            : 'dashboard-v2__withdrawal-form__redeem-all'
                        }
                        onClick={handleRedeemAll}
                        data-testid='withdrawal-redeem-all'
                      >
                        {isRedeemingAll ? 'Redeeming all' : 'Redeem all'}
                      </button>
                    )}
                  </div>
                  {canRedeemByUnits && (
                    <div
                      className={`dashboard-v2__withdrawal-form__mode-switch${
                        redeemMode === 'units'
                          ? ' dashboard-v2__withdrawal-form__mode-switch--units'
                          : ''
                      }`}
                      role='radiogroup'
                      aria-label='Redeem by amount or units'
                    >
                      <span
                        className='dashboard-v2__withdrawal-form__mode-switch-slider'
                        aria-hidden
                      />
                      <button
                        type='button'
                        role='radio'
                        aria-checked={redeemMode === 'amount'}
                        className={
                          redeemMode === 'amount'
                            ? 'dashboard-v2__withdrawal-form__mode-switch-btn dashboard-v2__withdrawal-form__mode-switch-btn--active'
                            : 'dashboard-v2__withdrawal-form__mode-switch-btn'
                        }
                        onClick={() => {
                          setRedeemMode('amount')
                          setWithdrawalUnitsInput('')
                        }}
                        data-testid='withdrawal-redeem-by-amount'
                      >
                        <span
                          className='dashboard-v2__withdrawal-form__mode-switch-icon'
                          aria-hidden
                        >
                          $
                        </span>
                        Amount
                      </button>
                      <button
                        type='button'
                        role='radio'
                        aria-checked={redeemMode === 'units'}
                        className={
                          redeemMode === 'units'
                            ? 'dashboard-v2__withdrawal-form__mode-switch-btn dashboard-v2__withdrawal-form__mode-switch-btn--active'
                            : 'dashboard-v2__withdrawal-form__mode-switch-btn'
                        }
                        onClick={() => {
                          setRedeemMode('units')
                          setWithdrawalAmountInput('')
                        }}
                        data-testid='withdrawal-redeem-by-units'
                      >
                        <span
                          className='dashboard-v2__withdrawal-form__mode-switch-icon'
                          aria-hidden
                        >
                          #
                        </span>
                        Units
                      </button>
                    </div>
                  )}
                  {redeemMode === 'units' && canRedeemByUnits && (
                    <div
                      className='dashboard-v2__withdrawal-form__nav-callout'
                      data-testid='withdrawal-current-nav'
                    >
                      <div className='dashboard-v2__withdrawal-form__nav-callout-main'>
                        <span className='dashboard-v2__withdrawal-form__nav-callout-label'>
                          Applicable NAV
                        </span>
                        <span className='dashboard-v2__withdrawal-form__nav-callout-value'>
                          {formatUsdFromCents(navPerUnitCents)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className='dashboard-v2__withdrawal-form__input-wrap'>
                    <span
                      className='dashboard-v2__withdrawal-form__input-prefix'
                      aria-hidden
                      key={redeemMode}
                    >
                      {redeemMode === 'amount' ? '$' : '#'}
                    </span>
                    {redeemMode === 'amount' ? (
                      <input
                        id='wr-withdrawal-amt'
                        className='dashboard-v2__withdrawal-form__input--prefixed'
                        type='text'
                        inputMode='decimal'
                        autoComplete='off'
                        placeholder='0.00'
                        value={withdrawalAmountInput}
                        onChange={(e) => setWithdrawalAmountInput(e.target.value)}
                      />
                    ) : (
                      <input
                        id='wr-withdrawal-units'
                        className='dashboard-v2__withdrawal-form__input--prefixed'
                        type='text'
                        inputMode='decimal'
                        autoComplete='off'
                        placeholder='0'
                        value={withdrawalUnitsInput}
                        onChange={(e) => setWithdrawalUnitsInput(e.target.value)}
                      />
                    )}
                  </div>
                  {redeemMode === 'amount' && maxWithdrawalCents > 0 && (
                    <span className='dashboard-v2__withdrawal-form__hint'>
                      Max {formatUsdFromCents(maxWithdrawalCents)}
                    </span>
                  )}
                  {redeemMode === 'units' && effectiveMaxWithdrawalUnits > 0 && (
                    <span className='dashboard-v2__withdrawal-form__hint'>
                      Max {formatUnits(effectiveMaxWithdrawalUnits)} units
                    </span>
                  )}
                </div>
              </section>

              <section
                className={`dashboard-v2__withdrawal-form__summary-strip${
                  canRedeemByUnits
                    ? ' dashboard-v2__withdrawal-form__summary-strip--with-units'
                    : ''
                }`}
              >
                <div className='dashboard-v2__withdrawal-form__summary-item'>
                  <span className='dashboard-v2__withdrawal-form__summary-label'>
                    Redemption amount
                  </span>
                  <span className='dashboard-v2__withdrawal-form__summary-value'>
                    {withdrawalPreviewDisplay}
                  </span>
                </div>
                <div className='dashboard-v2__withdrawal-form__summary-item'>
                  <span className='dashboard-v2__withdrawal-form__summary-label'>
                    Remaining balance
                  </span>
                  <span
                    className='dashboard-v2__withdrawal-form__summary-value'
                    data-testid='withdrawal-remaining-balance'
                  >
                    {remainingMoneyDisplay}
                  </span>
                </div>
                {canRedeemByUnits ? (
                  <div className='dashboard-v2__withdrawal-form__summary-item'>
                    <span className='dashboard-v2__withdrawal-form__summary-label'>
                      Remaining units
                    </span>
                    <span
                      className='dashboard-v2__withdrawal-form__summary-value'
                      data-testid='withdrawal-remaining-units'
                    >
                      {remainingUnitsDisplay}
                    </span>
                  </div>
                ) : null}
              </section>

              {exceedsMaxWithdrawal && (
                <div
                  className='dashboard-v2__withdrawal-form__amount-warning'
                  role='alert'
                  data-testid='withdrawal-exceeds-max-warning'
                >
                  <PendingIcon classes={['info-icon']} width='22px' height='22px' />
                  <span>
                    {redeemMode === 'amount' ? (
                      <>
                        The amount entered is greater than the maximum redemption amount of{' '}
                        {formatUsdFromCents(maxWithdrawalCents)}. Enter an amount up to that limit
                        to continue.
                      </>
                    ) : (
                      <>
                        The units entered exceed the maximum of{' '}
                        {formatUnits(effectiveMaxWithdrawalUnits)} units. Enter units up to that
                        limit to continue.
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className='dashboard-v2__withdrawal-form__footer'>
            <p className='dashboard-v2__withdrawal-form__footer-note'>
              Submitting will save your request.
            </p>
            <div className='dashboard-v2__withdrawal-form__footer-actions'>
              <button
                type='button'
                className='secondary'
                onClick={resetAndClose}
                disabled={submitting || showSuccessNotice}
              >
                Cancel
              </button>
              <button
                type='button'
                onClick={handleSubmit}
                disabled={
                  loading ||
                  submitting ||
                  !amountValid ||
                  !opportunity ||
                  showSuccessNotice ||
                  hasPendingRedemption
                }
              >
                {submitting ? 'Placing…' : 'Place redemption request'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <WithdrawalNoticeModal
        variant='success'
        isOpen={showSuccessNotice}
        onClose={() => {
          void handleDismissSuccessNotice()
        }}
        testId='redemption-success-notice'
      />
    </div>
  )
}
